import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus, Save, X, Eye, Calendar, Tag as TagIcon } from 'lucide-react';
import { apiClient } from '../context/AuthContext';
import toast from 'react-hot-toast';
import RichTextEditor from './RichTextEditor';
import SEOEditor from './SEOEditor';
import ImageUploader from './ImageUploader';

export default function BlogManager() {
  const [view, setView] = useState('posts'); // posts or categories
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content'); // content or seo

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data } = await apiClient.get('/blog/posts?status=all');
      setPosts(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await apiClient.get('/blog/categories');
      setCategories(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const handleSavePost = async () => {
    try {
      if (editingPost.id) {
        await apiClient.put(`/blog/posts/${editingPost.id}`, editingPost);
        toast.success('Article mis à jour !');
      } else {
        await apiClient.post('/blog/posts', editingPost);
        toast.success('Article créé !');
      }
      setShowPostModal(false);
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      await apiClient.delete(`/blog/posts/${postId}`);
      toast.success('Article supprimé');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory.slug && categories.find(c => c.slug === editingCategory.slug && c !== editingCategory)) {
        await apiClient.put(`/blog/categories/${editingCategory.slug}`, editingCategory);
        toast.success('Catégorie mise à jour !');
      } else {
        await apiClient.post('/blog/categories', editingCategory);
        toast.success('Catégorie créée !');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCategory = async (slug) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    
    try {
      await apiClient.delete(`/blog/categories/${slug}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const openPostModal = (post = null) => {
    if (post) {
      setEditingPost(post);
    } else {
      setEditingPost({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        category: categories[0]?.slug || 'conseils',
        featured_image: '',
        tags: [],
        author: 'Joey Cyan Team',
        status: 'draft',
        reading_time: 5,
        meta_title: '',
        meta_description: '',
        og_image: ''
      });
    }
    setActiveTab('content');
    setShowPostModal(true);
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
    } else {
      setEditingCategory({
        name: '',
        slug: '',
        description: '',
        meta_title: '',
        meta_description: '',
        og_image: ''
      });
    }
    setShowCategoryModal(false);
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion du Blog</h2>
          <p className="text-sm text-gray-500 mt-1">
            {posts.length} articles • {categories.length} catégories
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setView(view === 'posts' ? 'categories' : 'posts')}
            className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            {view === 'posts' ? 'Gérer les catégories' : 'Gérer les articles'}
          </button>
          {view === 'posts' ? (
            <button
              onClick={() => openPostModal()}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#00aaff] text-white rounded-xl font-semibold hover:bg-[#0088dd] transition-colors"
            >
              <Plus size={18} />
              Nouvel article
            </button>
          ) : (
            <button
              onClick={() => openCategoryModal()}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#00aaff] text-white rounded-xl font-semibold hover:bg-[#0088dd] transition-colors"
            >
              <Plus size={18} />
              Nouvelle catégorie
            </button>
          )}
        </div>
      </div>

      {/* Posts List */}
      {view === 'posts' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Article</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Catégorie</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">{post.title}</p>
                      <p className="text-xs text-gray-500 mt-1">/{post.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                      {post.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                      post.status === 'published' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {post.status === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => openPostModal(post)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Categories List */}
      {view === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <div key={cat.slug} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-900">{cat.name}</h3>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditingCategory(cat); setShowCategoryModal(true); }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat.slug)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2">{cat.description || 'Aucune description'}</p>
              <code className="text-xs text-gray-500">/{cat.slug}</code>
            </div>
          ))}
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && editingPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-5xl w-full my-8">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-900">
                {editingPost.id ? 'Modifier l\'article' : 'Nouvel article'}
              </h3>
              <button onClick={() => setShowPostModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-gray-100 flex gap-2">
              <button
                onClick={() => setActiveTab('content')}
                className={`px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors ${
                  activeTab === 'content'
                    ? 'bg-[#00aaff] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Contenu
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`px-4 py-2 font-semibold text-sm rounded-t-lg transition-colors ${
                  activeTab === 'seo'
                    ? 'bg-[#00aaff] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                SEO
              </button>
            </div>

            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {activeTab === 'content' && (
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Titre</label>
                    <input
                      type="text"
                      value={editingPost.title}
                      onChange={(e) => {
                        const newTitle = e.target.value;
                        setEditingPost({
                          ...editingPost,
                          title: newTitle,
                          slug: editingPost.id ? editingPost.slug : generateSlug(newTitle)
                        });
                      }}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      placeholder="Titre de l'article"
                    />
                  </div>

                  {/* Slug */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Slug (URL)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">joeycyan.com/blog/</span>
                      <input
                        type="text"
                        value={editingPost.slug}
                        onChange={(e) => setEditingPost({...editingPost, slug: e.target.value})}
                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                        placeholder="mon-article"
                      />
                    </div>
                  </div>

                  {/* Category & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Catégorie</label>
                      <select
                        value={editingPost.category}
                        onChange={(e) => setEditingPost({...editingPost, category: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      >
                        {categories.map(cat => (
                          <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Statut</label>
                      <select
                        value={editingPost.status}
                        onChange={(e) => setEditingPost({...editingPost, status: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      >
                        <option value="draft">Brouillon</option>
                        <option value="published">Publié</option>
                      </select>
                    </div>
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Extrait</label>
                    <textarea
                      value={editingPost.excerpt}
                      onChange={(e) => setEditingPost({...editingPost, excerpt: e.target.value})}
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      placeholder="Court résumé de l'article..."
                    />
                  </div>

                  {/* Featured Image */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Image mise en avant</label>
                    <ImageUploader
                      images={editingPost.featured_image ? [editingPost.featured_image] : []}
                      onChange={(images) => setEditingPost({...editingPost, featured_image: images[0] || ''})}
                      maxImages={1}
                    />
                  </div>

                  {/* Content Editor */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Contenu</label>
                    <RichTextEditor
                      value={editingPost.content}
                      onChange={(value) => setEditingPost({...editingPost, content: value})}
                    />
                  </div>

                  {/* Reading Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Temps de lecture (min)</label>
                      <input
                        type="number"
                        value={editingPost.reading_time || 5}
                        onChange={(e) => setEditingPost({...editingPost, reading_time: parseInt(e.target.value)})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Auteur</label>
                      <input
                        type="text"
                        value={editingPost.author}
                        onChange={(e) => setEditingPost({...editingPost, author: e.target.value})}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'seo' && (
                <SEOEditor
                  metaTitle={editingPost.meta_title}
                  metaDescription={editingPost.meta_description}
                  slug={editingPost.slug}
                  ogImage={editingPost.og_image}
                  defaultTitle={editingPost.title}
                  defaultDescription={editingPost.excerpt}
                  isProductContext={true}
                  onChange={(seoData) => setEditingPost({...editingPost, ...seoData})}
                />
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowPostModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSavePost}
                className="flex-1 bg-[#00aaff] text-white py-3 rounded-xl font-semibold hover:bg-[#0088dd] flex items-center justify-center gap-2"
              >
                <Save size={18} />
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && editingCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCategory.slug ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-5 gap-4">
                <div className="col-span-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Nom</label>
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({
                      ...editingCategory,
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Emoji</label>
                  <input
                    type="text"
                    value={editingCategory.icon || ''}
                    onChange={(e) => setEditingCategory({...editingCategory, icon: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-2xl text-center outline-none focus:border-[#00aaff]"
                    placeholder="📝"
                    maxLength={2}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase">Description</label>
                <textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff]"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveCategory}
                className="flex-1 bg-[#00aaff] text-white py-3 rounded-xl font-semibold hover:bg-[#0088dd]"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
